/**
 * Utility for resolving local image URLs on mobile platforms (iOS, Android, Windows).
 *
 * Mobile platforms return image URLs that lack protocols (e.g., bare filenames like
 * "4EF86918-8A06-4D0A-A874-2FC8E3302E6B.png") which need to be resolved to full
 * localhost storage URLs for fetching.
 *
 * Based on jedy-sdk's getImageUrl/useLocalServer logic.
 */

/**
 * Mobile localhost storage base URL
 * Both iOS and Android serve images via this endpoint
 */
const MOBILE_STORAGE_BASE_URL = 'https://localhost:4000/storage?file=';

/**
 * Protocols that indicate a fully-formed URL (doesn't need local server resolution)
 */
const VALID_PROTOCOLS = ['http://', 'https://', 'blob:', 'data:'];

/**
 * Check if a URL needs to be resolved via local storage server.
 * URLs that don't start with http://, https://, blob:, or data: need resolution.
 * This matches the SDK's useLocalServer logic.
 */
export const needsLocalServerResolution = (url: string): boolean => {
  if (!url) return false;
  return VALID_PROTOCOLS.every(protocol => !url.startsWith(protocol));
};

/**
 * Check if URL is a localhost/local server URL that external APIs cannot access.
 * Also handles mobile-style URLs that lack protocols.
 */
export const isLocalUrl = (url: string): boolean => {
  // Check for mobile URLs that need local server resolution
  if (needsLocalServerResolution(url)) {
    return true;
  }

  try {
    const urlObj = new URL(url);
    return urlObj.hostname === 'localhost' || urlObj.hostname === '127.0.0.1' || urlObj.hostname === '0.0.0.0' || urlObj.hostname.endsWith('.local');
  } catch {
    return false;
  }
};

/**
 * Convert a mobile URL (lacking protocol) to a full localhost storage URL.
 * Mobile apps serve images via localhost:4000/storage?file=FILENAME
 *
 * This matches the SDK's getImageUrl logic - if URL doesn't start with
 * http://, https://, blob:, or data:, it needs to be resolved.
 *
 * @param url - The URL or filename to resolve
 * @returns The resolved URL (full storage URL for mobile URLs, original URL otherwise)
 */
export const resolveLocalUrl = (url: string): string => {
  if (needsLocalServerResolution(url)) {
    const storageUrl = `${MOBILE_STORAGE_BASE_URL}${encodeURIComponent(url)}`;
    return storageUrl;
  }
  return url;
};

/**
 * Fetch a local URL and convert to base64 data URL
 * Used to convert localhost images to uploadable format
 * Handles both full localhost URLs and bare filenames (iOS)
 */
export const fetchLocalUrlAsBase64 = async (url: string): Promise<string> => {
  // Resolve bare filenames to full storage URLs
  const resolvedUrl = resolveLocalUrl(url);
  const response = await fetch(resolvedUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch local image: ${response.status}`);
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to convert image to base64'));
    reader.readAsDataURL(blob);
  });
};

export default resolveLocalUrl;
